import React, { Fragment, useState, useEffect } from "react";
import { Col, Row, Table, Button, Modal, ModalBody, ModalHeader, Label } from "reactstrap";
import { CircularProgress } from "../UI/CircularProgress/CircularProgress";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { showToast } from "../../utils/helper";
import { InvestModal } from "../../components/Modal/InvestModal/InvestModal";
import { InvestBuyModal } from "../Modal/InvestBuyModal/InvestBuyModal";
import { InvestTable } from "./InvestTable/InvestTable";
import { fetchOffers, fetchFunds, updateFund } from "../../services/api/inout";
import { getEarnings, takeOffer } from "../../services/api/inout";
import { getSession, getCurrentAccount, getSavedPersonInfo, getTexts } from "../../reducers/selectors";
import { Container } from "../UI/Container/Container";
import { TitleLabel } from "../UI/TitleLabel/TitleLabel";
import { RadioButton, RadioButtonGroup } from "../UI/RadioButton/RadioButton";
import { Icons } from "../../components/UI/Icon/Icon";
import style from "./Invest.scss";

const allowedCurrencies = {
  ALL: "ALL",
  PYG: "PYG",
  USD: "USD",
};

export const Invest = () => {
  const [modal, setModal] = useState(false);
  const [item, setItem] = useState();
  const [activeOffers, setActiveOffers] = useState({ activeOffers: [], activeFunds: [] });
  const [availableOffers, setAvailableOffers] = useState([]);

  const [loadingOffers, setLoadingOffers] = useState(true);
  const [loadingFunds, setLoadingFunds] = useState(true);

  const [funds, setFunds] = useState([]);
  const [offers, setOffers] = useState([]);

  const [filteredFunds, setFilteredFunds] = useState([]);
  const [filteredOffers, setFilteredOffers] = useState([]);

  const currentAccount = useSelector(getCurrentAccount);
  const [activeCurrency, setActiveCurrency] = useState(allowedCurrencies.ALL);

  const texts = useSelector(getTexts);

  useEffect(() => {
    if (currentAccount.id) {
      loadOffers();
      loadFunds();
    }
  }, [currentAccount]);

  useEffect(() => {
    if (offers) {
      setFilteredOffers(() =>
        offers.filter((offer) =>
          activeCurrency === allowedCurrencies.ALL
            ? offer
            : offer.AssetInformation.CurrencyISO === activeCurrency
        )
      );
    }
    if (funds) {
      setFilteredFunds(() =>
        funds.filter((fund) =>
          activeCurrency === allowedCurrencies.ALL
            ? fund
            : fund.AssetInformation.CurrencyISO === activeCurrency
        )
      );
    }
  }, [activeCurrency, offers, funds]);

  const loadOffers = () => {
    if (currentAccount.id) {
      setLoadingOffers(() => true);
      setOffers(() => []);
      fetchOffers(currentAccount.id)
        .then(({ response }) => response)
        .then(({ result, SdtOffers }) => {
          if (result.Code === 400) {
            showToast("Hubo un error obteniendo ofertas de inversión", true);
          } else {
            setOffers(() => SdtOffers.OffersItems);
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setLoadingOffers(() => false);
        });
    }
  };

  const loadFunds = () => {
    if (currentAccount.id) {
      setLoadingFunds(() => true);
      setFunds(() => []);
      fetchFunds(currentAccount.id)
        .then(({ response }) => response)
        .then(({ result, funds }) => {
          if (result.Code === 400) {
            showToast("Hubo un error obteniendo los fondos disponibles", true);
          } else {
            setFunds(() => funds);
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setLoadingFunds(() => false);
        });
    }
  };

  useEffect(() => {
    let outstanding = [];
    let available = [];
    activeOffers.activeOffers.forEach((offer) => {
      offer.featured === true ? outstanding.push(offer) : available.push(offer);
    });
    setAvailableOffers(available);
  }, [activeOffers.activeOffers]);

  const toggle = (item) => {
    setModal((prev) => !prev);
    setItem(item);
  };

  const handleActiveCurrency = (name) => {
    setActiveCurrency(allowedCurrencies[name]);
  };

  const EmptyInvests = () => {
    return <div className={style.emptyMessage}>No hay inversiones disponibles.</div>;
  };

  return (
    <Fragment>
      <Row className={style.headerTitle}>
        <Label>Invertir</Label>
      </Row>
      <Row>
        <Col className={style.wrapperCol} lg="12">
          <Fragment>
            <Container className={style.containerCurrency}>
              <Col>
                <TitleLabel className={style.TitleLabel} label="Inversiones disponibles" />
                <TitleLabel className={style.TitleLabelMobile} label="Inversiones" />
              </Col>
              <Col className={style.colRadioButton}>
                <RadioButtonGroup>
                  <RadioButton
                    label="Todos"
                    onClick={handleActiveCurrency}
                    name={allowedCurrencies.ALL}
                    active={activeCurrency === allowedCurrencies.ALL}
                  />
                  <RadioButton
                    label="Guaraníes"
                    onClick={handleActiveCurrency}
                    name={allowedCurrencies.PYG}
                    active={activeCurrency === allowedCurrencies.PYG}
                  />
                  <RadioButton
                    label="Dólares"
                    onClick={handleActiveCurrency}
                    name={allowedCurrencies.USD}
                    active={activeCurrency === allowedCurrencies.USD}
                  />
                </RadioButtonGroup>
              </Col>
            </Container>
            {currentAccount.hasSEN || loadingOffers || loadingFunds ? null : (
              <div className={style.disclaimer} colSpan={7}>
                <div className="iconCol">
                  <Icons.InfoIcon size="md" className={style.icon} />
                </div>
                <div className="textCol">
                  <p className={style.bold}>{texts.notes.sen_warning_bold}</p>
                  <p className={style.text}>{texts.notes.sen_warning_text}</p>
                </div>
              </div>
            )}
            <Container className={style.container}>
              {loadingOffers || loadingFunds ? (
                <CircularProgress />
              ) : (
                <Fragment>
                  {availableOffers.length > 0 || funds.length > 0 ? (
                    <InvestTable offers={filteredOffers} funds={filteredFunds} toggle={toggle} />
                  ) : (
                    <EmptyInvests />
                  )}
                </Fragment>
              )}
            </Container>
          </Fragment>
          {modal && <InvestModal modal={modal} toggle={toggle} item={item} />}
        </Col>
      </Row>
    </Fragment>
  );
};
